// PDP gallery (10 Oct 2026, docs/research/pdp-galerij-2026-10-10.md). Loaded as a module on every B2C product page.
// 1. Desktop (two columns, >= 750 px): the buying column follows the scroll. A column that fits the window sticks
//    under the header; a taller column scrolls until its end is in view and then sticks there, so nothing in it
//    is ever out of reach.
// 2. Tap or click on a gallery photo opens the existing zoom view (PhotoSwipe, pinch/zoom, Esc closes). A drag or
//    swipe does not open it (Flickity marks those clicks as prevented).
// 3. The Fritz Bed desktop grid (snippets/astra-pdp-grid.liquid): the print tile follows a click on a print
//    swatch; a click on any tile opens the same zoom view with the four visible photos.
// 4. Mobile: the next photo (its edge shows beside the current one) loads right after the first photo.
import { trapFocus, removeTrapFocus } from 'util.a11y'

const root = document.querySelector('.b2c-product')

// 1. Sticky buying column ------------------------------------------------------------------------------------
function initStickyColumn() {
  const column = root?.querySelector('.b2c-product-choice')
  if (!column) return
  const twoColumns = matchMedia('(min-width: 750px)')
  const headerOffset = () => {
    // The theme header sticks to the top; measure it instead of assuming a height.
    const header = document.querySelector('.site-header')
    if (!header) return 0
    let el = header
    while (el && el !== document.body) {
      const position = getComputedStyle(el).position
      if (position === 'sticky' || position === 'fixed') return Math.max(0, Math.round(el.getBoundingClientRect().bottom))
      el = el.parentElement
    }
    return 0
  }
  let frame = 0
  const update = () => {
    frame = 0
    if (!twoColumns.matches) { root.removeAttribute('data-b2c-sticky'); return }
    const gap = 16
    const top = headerOffset() + gap
    const fits = column.offsetHeight + top + gap <= innerHeight
    const value = fits ? top : Math.min(top, innerHeight - column.offsetHeight - gap)
    root.style.setProperty('--b2c-choice-top', `${Math.round(value)}px`)
    root.setAttribute('data-b2c-sticky', fits ? 'top' : 'bottom')
  }
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update) }
  new ResizeObserver(schedule).observe(column)
  addEventListener('resize', schedule, { passive: true })
  twoColumns.addEventListener('change', schedule)
  update()
}

// Shared zoom view for the grid ---------------------------------------------------------------------------------
let photoswipeReady
function loadPhotoswipe() {
  photoswipeReady ||= Promise.all([import('vendor.photoswipe'), import('vendor.photoswipe-ui-default')])
  return photoswipeReady
}

function openZoom(links, index, trigger) {
  const element = document.querySelector('.pswp')
  if (!element) { location.href = links[index].href; return }
  return loadPhotoswipe().then(() => {
    const items = links.map((link) => {
      const img = link.querySelector('img')
      return { src: link.href, w: Number(link.dataset.zoomW) || 1366, h: Number(link.dataset.zoomH) || 1708, msrc: img?.currentSrc || img?.src, el: img }
    })
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
    document.body.classList.add('photoswipe-open')
    const gallery = new PhotoSwipe(element, PhotoSwipeUI_Default, items, {
      index,
      history: false,
      shareEl: false,
      captionEl: false,
      counterEl: false,
      preloaderEl: false,
      closeOnScroll: false,
      pinchToClose: false,
      tapToToggleControls: false,
      escKey: true,
      showAnimationDuration: reduced ? 0 : 250,
      hideAnimationDuration: reduced ? 0 : 250,
      getThumbBoundsFn(i) {
        const rect = items[i].el.getBoundingClientRect()
        return { x: rect.left, y: rect.top + scrollY, w: rect.width }
      }
    })
    gallery.listen('afterInit', () => trapFocus(gallery.template))
    gallery.listen('destroy', () => {
      removeTrapFocus()
      document.body.classList.remove('photoswipe-open')
      const current = links[gallery.getCurrentIndex()] || trigger
      current?.focus({ preventScroll: true })
    })
    gallery.init()
  }).catch(() => { location.href = links[index].href })
}

// 2. Tap/click on a gallery photo opens the existing zoom view ---------------------------------------------------
function initTapToZoom() {
  if (!root) return
  root.addEventListener('click', (event) => {
    if (event.defaultPrevented) return
    const wrap = event.target.closest('product-images .product-main-slide .image-wrap')
    if (!wrap || event.target.closest('button, a, video, .product__video-wrapper')) return
    const button = wrap.querySelector('.js-photoswipe__zoom') || wrap.closest('.product-main-slide')?.querySelector('.js-photoswipe__zoom')
    if (!button) return
    event.preventDefault()
    button.click()
  })
}

// 3. Fritz Bed desktop grid ------------------------------------------------------------------------------------
// Four tiles; the print tile (top right, next to the swatches) follows a click on a print swatch, and on the Fritz Bed
// also the scene tile (bottom left, 17 Oct). Programmatic variant changes (page load, size choice) leave them alone,
// so the start photos stay until a click.
function initGrid() {
  const grid = root?.querySelector('[data-b2c-pdp-grid]')
  if (!grid) return
  const tiles = [...grid.querySelectorAll('[data-b2c-pdp-print]')].map((tile) => [...tile.querySelectorAll('.b2c-pdp-grid__cell')])
  const show = (print) => tiles.forEach((layers) => {
    const next = layers.find((layer) => layer.dataset.print === print)
    if (!next || !next.hidden) return
    const img = next.querySelector('img')
    if (img) img.loading = 'eager'
    layers.forEach((layer) => { layer.hidden = layer !== next })
  })
  document.addEventListener('change', (event) => {
    const input = event.target
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return
    const picker = input.closest('kit-variant-picker')
    if (!picker || picker.dataset.productId !== root.dataset.productId) return
    show(input.value)
  })
  // Video tile: only inserted on desktop, muted and inline; with reduced motion it waits for a click on play.
  if (matchMedia('(min-width: 990px)').matches) {
    grid.querySelectorAll('[data-b2c-pdp-video]').forEach((cell) => {
      const video = cell.querySelector('template')?.content.querySelector('video')?.cloneNode(true)
      if (!video) return
      video.muted = true
      video.playsInline = true
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) { video.autoplay = false; video.controls = true }
      cell.append(video)
    })
  }
  grid.addEventListener('click', (event) => {
    const link = event.target.closest('a.b2c-pdp-grid__cell')
    if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey) return
    event.preventDefault()
    const links = [...grid.querySelectorAll('a.b2c-pdp-grid__cell:not([hidden])')]
    openZoom(links, links.indexOf(link), link)
  })
}

// 5. Content photos further down (The Details) and the wash card in the buying column ------------------------------
// 17 Oct 2026 (Clarity on Ads visitors): taps on these photos did nothing. They now open the same zoom view; the photos
// of one block open together, so a visitor can swipe through them. Largest srcset entry is the zoom image.
function initContentZoom() {
  const selector = '.b2c-photo-details-v31 figure img, .astra-bed-wash-proof img'
  const asLink = (img) => {
    const best = (img.getAttribute('srcset') || '').split(',').map((part) => part.trim().split(/\s+/))
      .map(([url, size]) => ({ url, w: parseInt(size, 10) || 0 })).sort((x, y) => y.w - x.w)[0]
    const w = best?.w || img.naturalWidth || 1080
    const ratio = (img.naturalHeight && img.naturalWidth) ? img.naturalHeight / img.naturalWidth : (Number(img.getAttribute('height')) / Number(img.getAttribute('width'))) || 1
    return { href: best?.url || img.currentSrc || img.src, dataset: { zoomW: w, zoomH: Math.round(w * ratio) }, querySelector: () => img, focus: () => {} }
  }
  document.querySelectorAll(selector).forEach((img) => { img.style.cursor = 'zoom-in' })
  document.addEventListener('click', (event) => {
    const img = event.target.closest?.(selector)
    if (!img || event.defaultPrevented || img.closest('a')) return
    event.preventDefault()
    const block = img.closest('.b2c-photo-details-v31, .astra-bed-wash-proof')
    const imgs = [...block.querySelectorAll('figure img')]
    openZoom(imgs.map(asLink), imgs.indexOf(img), img)
  })
}

// 4. Mobile peek: the edge of the next photo is visible, so load that photo once the first one is in --------------
function initPeek() {
  const images = root?.querySelector('product-images[data-has-slideshow="true"]')
  if (!images || !matchMedia('(max-width: 749px)').matches) return
  customElements.whenDefined('product-images').then(() => {
    const slides = [...images.querySelectorAll('.product-main-slide')]
    const first = images.querySelector('.product-main-slide.is-selected') || slides[0]
    const next = slides[slides.indexOf(first) + 1]
    if (!next || typeof images.loadGalleryImage !== 'function') return
    const warm = () => (window.requestIdleCallback || setTimeout)(() => images.loadGalleryImage(next), { timeout: 1500 })
    const img = first?.querySelector('img')
    if (!img || (img.complete && img.naturalWidth > 0)) warm()
    else img.addEventListener('load', warm, { once: true })
  })
}

initStickyColumn()
initPeek()
initTapToZoom()
initGrid()
initContentZoom()
